import { useEffect, useState } from "react";
import { Coffee, UtensilsCrossed, Cookie, Moon, X } from "lucide-react";
import { api } from "@/lib/api";

const META = {
  breakfast: { label: "Breakfast", icon: Coffee, bg: "#FFE600", fg: "#0A0A0A" },
  lunch: { label: "Lunch", icon: UtensilsCrossed, bg: "#00CC66", fg: "#FFFFFF" },
  snacks: { label: "Evening Snacks", icon: Cookie, bg: "#00E5FF", fg: "#0A0A0A" },
  dinner: { label: "Dinner", icon: Moon, bg: "#FF4D4D", fg: "#FFFFFF" },
};
const ORDER = ["breakfast", "lunch", "snacks", "dinner"];
const DAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const FALLBACK = {
  breakfast: { time: "7:30 – 9:30 AM", items: ["Idli & Vada with Sambar", "Poha / Upma", "Bread & Omelette", "Milk / Tea / Coffee"] },
  lunch: { time: "12:30 – 2:30 PM", items: ["Rice + Sambar + Rasam", "Chapati / Roti", "Sabzi", "Curd + Pickle"] },
  snacks: { time: "5:00 – 6:30 PM", items: ["Samosa / Pakoda", "Fruit", "Tea / Coffee"] },
  dinner: { time: "8:00 – 10:00 PM", items: ["Chapati / Rice", "Dal / Rajma", "Sabzi + Curd", "Non-Veg (Wed & Sun)"] },
};

export default function FoodMenu() {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(FALLBACK);
  const today = DAYS[new Date().getDay()];

  useEffect(() => {
    api.get("/menu").then((r) => r.data && setMenu(r.data)).catch(() => {});
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-6 py-12" data-testid="food-menu-section">
      <div
        className="border-2 border-[#0A0A0A] rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        style={{ backgroundColor: "#FFE600", boxShadow: "6px 6px 0px 0px rgba(10,10,10,1)" }}
      >
        <div className="max-w-xl">
          <span className="inline-block bg-[#0A0A0A] text-[#FFE600] px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] rounded-full">
            Today · {today}
          </span>
          <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-3">
            What's cooking today?
          </h2>
          <p className="mt-3 font-medium text-base">
            Fresh, hygienic, home-style meals — breakfast, lunch, snacks & dinner. See the full spread and mess timings.
          </p>
        </div>
        <button
          type="button"
          data-testid="food-menu-open-button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-3 font-heading font-black text-lg md:text-xl px-8 py-5 border-2 border-[#0A0A0A] rounded-full bg-[#FF4D4D] text-white shadow-brutal hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-transform"
        >
          <UtensilsCrossed size={22} strokeWidth={2.5} /> TODAY'S MENU
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-[#0A0A0A]/70 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="food-menu-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#F8F9FA] border-2 border-[#0A0A0A] rounded-2xl shadow-brutal-lg"
          >
            <div className="sticky top-0 flex items-center justify-between px-6 py-4 bg-[#FFE600] border-b-2 border-[#0A0A0A]">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em]">Today · {today}</div>
                <h3 className="font-heading font-black text-2xl md:text-3xl tracking-tighter mt-1">
                  Vasavi Boys Hostel — Menu
                </h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="food-menu-close-button"
                aria-label="Close menu"
                className="p-2 border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow"
              >
                <X size={18} strokeWidth={2.5} />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {ORDER.map((key) => {
                const meal = menu[key] || FALLBACK[key];
                const meta = META[key];
                const Icon = meta.icon;
                return (
                  <div
                    key={key}
                    data-testid={`food-menu-${key}`}
                    style={{
                      backgroundColor: meta.bg, color: meta.fg,
                      border: "2px solid #0A0A0A", borderRadius: "0.75rem",
                      boxShadow: "4px 4px 0px 0px rgba(10,10,10,1)", padding: "1.5rem",
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <Icon size={30} strokeWidth={2.5} />
                      <span className="text-xs font-bold uppercase tracking-[0.2em] px-2 py-1 border-2 border-current rounded-full">
                        {meal.time}
                      </span>
                    </div>
                    <h4 className="font-heading font-black text-2xl mt-3">{meta.label}</h4>
                    <ul className="mt-3 space-y-1.5 list-disc pl-5 font-medium text-sm">
                      {(meal.items || []).map((it, i) => <li key={i}>{it}</li>)}
                    </ul>
                  </div>
                );
              })}
            </div>
            <div className="px-6 pb-6 text-xs font-medium text-[#4A4A4A]">
              Menu rotates weekly. Fasting / diet-friendly plates available on request from the mess-in-charge.
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
